/* eslint-disable max-lines */
import { getCurrentDateTime } from '@utils/moderatorHelper';
import { itemTypes, programmeTypes, topologyLevels } from '@constants/index';
import { educatorTypes } from '../constants';

const getData = (result) => {
  const data = {};
  const { programme, items, name } = result;
  const languages = result.languages.map((lang) => ({
    ...lang,
    code: lang.id
  }));
  const { value = {} } = items?.length ? items[0] : {};
  data.programmeType = programme.programme_type;
  data.spclClassSlug = programme.slug;
  data.isFreeTest =
    data.programmeType === programmeTypes.TESTSERIES &&
    !programme.for_subscription;
  data.speciaClassDuration = value.live_class?.duration;
  data.spclClassUID = value.uid;
  data.contentSlideLanguages = languages.filter(
    (lang) => lang.language_type === 2
  );
  data.liveAt = value.live_class?.live_at;
  data.videoURL = value.live_class?.video_url;
  data.selectedClasses = result.classes;
  data.specialClassscheduledDateTime = value.live_class?.live_at;
  data.forPlus = result.for_plus;
  data.isSpecial = !!(
    data.forPlus &&
    !programme.for_subscription &&
    data.programmeType === programmeTypes.COURSE
  );
  data.title = data.isSpecial ? value.title : name;
  data.visibleFrom = programme.visible_at;
  data.registrationClosesOn = programme.applications_close_at;
  data.description = result.description.substring(
    0,
    data.programmeType !== programmeTypes.TESTSERIES ? 500 : 2000
  );
  data.courseUID = result.uid;
  data.thumbnail = result.thumbnail;
  data.slides = {
    withAnnotations: value.live_class?.slides_pdf?.with_annotation,
    withoutAnnotations: value.live_class?.slides_pdf?.no_annotation
  };
  data.selectedLanguages = languages.filter((lang) => lang.language_type === 1);
  data.selectedGoal = [programme.goal];
  data.courseType = result.course_type;
  data.selectedTopics = result.topics;
  data.selectedExams = result.exams;
  data.preview = result.intro_video || result.programme?.intro_video;
  data.slug = programme.slug;
  data.courseState = programme.state;
  data.templateUID = programme.template_id;
  data.selectedEducators = programme.authors.map((author) => ({
    ...author,
    ...author.author_details,
    name: `${author.first_name} ${author.last_name}`
  }));
  data.sessions = items?.map((item) => ({
    name: item.value.title,
    uid: item.value.uid,
    author: { ...item.value.author, ...item.value.author.author_details },
    rank: item.rank,
    type: item.type,
    quizSecondaryType: item.value.quiz_secondary_type,
    state: item.value.state || item.value.live_class.state,
    thumbnail: item.value.live_class?.post_thumbnail,
    duration: item.value.duration || item.value.live_class.duration,
    scheduledLiveAt:
      item.type === itemTypes.POST
        ? item.value.live_class.live_at
        : item.value.start_time,
    liveAt:
      item.type === itemTypes.POST
        ? item.value.live_class.live_at
        : item.value.start_time,
    selectedConcepts: item.value.topologies,
    contentState: item.value.content_state || null,
    quizType: item.value.quiz_type,
    availableLanguage: item.value.available_language || [],
    educatorSlides:
      item.value.live_class?.educator_slides ||
      item.value.live_class?.drive_details,
    learnerNotes: item.value.live_class?.learner_notes,
    practice_quiz: item.value.live_class?.practice_quiz
  }));
  return data;
};

const initialState = {
  isSpecialClass: false,
  programmeType: '',
  courseState: '',
  isSubmitting: false,
  isFreeTest: false,
  onBoardingPreferences: {},
  title: '',
  description: '',
  preview: '',
  selectedClasses: [],
  thumbnail: '',
  courseUID: '',
  forPlus: false,
  allLanguages: [],
  selectedLanguages: [],
  selectedGoal: [],
  specialClassscheduledDateTime: '', // special class
  visibleFrom: '',
  selectedTopics: [],
  selectedTopicGroups: [],
  selectedExams: [],
  selectedEducators: [],
  sessions: [],
  speciaClassDuration: '',
  spclClassUID: '',
  spclClassSlug: '',
  spclClassError: {},
  courseTypes: [],
  courseType: 0,
  coAuthors: [],
  deleting: false,
  createEditError: {},
  createEditSessionError: {},
  filter: {},
  editSessionError: {},
  newSession: {
    title: '',
    liveAt: '',
    selectedConcepts: [],
    selectedSubConcepts: [],
    selectedEducator: null,
    selectedCoAuthor: null,
    state: 0
  },
  liveClassData: {},
  courseDetails: {
    rescheduleDetails: {
      date: '',
      time: '',
      reasonCode: [],
      remarks: ''
    },
    discussionForum: {}
  },
  goliveDetails: null,
  feedBackOptionsForATemplateCourse: {},
  clashSessions: [],
  areSessionsClashing: false,
  schedule: {
    startsFrom: '',
    time: '',
    daysOfWeek: [0, 0, 0, 0, 0, 0, 0],
    duration: ''
  }
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case `${educatorTypes.FETCH_EDUCATOR_LEADERBOARD}_REQUEST`:
      return { ...state, isSubmitting: action.resetWithoutEmpty };

    case educatorTypes.UPDATE_SESSION_CONCEPTS:
    case educatorTypes.UPDATE_SESSION_VALUE:
    case educatorTypes.SET_SESSION_EDUCATOR:
      return {
        ...state,
        newSession: { ...state.newSession, [action.id]: action.value }
      };

    case educatorTypes.UPDATE_COURSE_AUTOCOMPLETE_FIELDS:
    case educatorTypes.UPDATE_COURSE_DROPDOWN_FIELDS:
    case educatorTypes.UPDATE_COURSE_DETAILS:
    case educatorTypes.UPDATE_CHECK_BOX:
      return {
        ...state,
        [action.id]: action.isSingleValue ? [action.value] : action.value
      };

    case educatorTypes.REMOVE_CONCEPT:
      return {
        ...state,
        newSession: {
          ...state.newSession,
          [action.key]: state.newSession[action.key].filter(
            (option) => option.uid !== action.value.uid
          )
        }
      };

    case educatorTypes.REMOVE_TAG:
      return {
        ...state,
        [action.id]: state[action.id]?.filter(
          (option) => option[action.key] !== action.value[action.key]
        )
      };

    case `${educatorTypes.EDIT_SESSION}_SUCCESS`:
    case `${educatorTypes.EDIT_SESSION}_FAILURE`:
      return {
        ...state,
        isSubmitting: false,
        editSessionError: action.error
      };

    case `${educatorTypes.STATE_CHANGE}_SUCCESS`:
      return {
        ...state,
        courseState: action.result.programme.state,
        isSubmitting: false
      };

    case educatorTypes.SETUP_EDIT_SESSION:
      return {
        ...state,
        newSession: action.value
          ? {
              ...state.newSession,
              ...action.value,
              title: action.value.name || action.value.title,
              selectedEducator: action.value.author
                ? {
                    ...action.value.author,
                    name: `${action.value.author.first_name} ${action.value.author.last_name}`
                  }
                : null,
              duration: action.value.duration,
              liveAt: getCurrentDateTime(action.value.liveAt),
              type: action.value.type,
              uid: action.value.uid,
              selectedConcepts: action.value.selectedConcepts.filter(
                (concept) => concept.level === topologyLevels.CONCEPT
              ),
              selectedSubConcepts: action.value.selectedConcepts.filter(
                (concept) => concept.level === topologyLevels.SUBCONCEPT
              ),
              state: action.value.state
            }
          : initialState.newSession
      };

    case `${educatorTypes.FETCH_ALL_LANGUAGES}_SUCCESS`:
    case `${educatorTypes.FETCH_CO_AUTHOR}_SUCCESS`:
    case `${educatorTypes.FETCH_COURSE_TYPES}_SUCCESS`:
    case educatorTypes.SET_LESSON_REFS:
    case educatorTypes.SET_COURSE_TYPE:
    case `${educatorTypes.FETCH_GOLIVE_DETAILS}_SUCCESS`:
    case `${educatorTypes.FETCH_EDU_LIVE_CLASS_URL}_SUCCESS`:
    case `${educatorTypes.GET_FEEDBACK_FOR_COURSE_TEMPLATE}_SUCCESS`:
    case educatorTypes.SET_CREATE_EDIT_SESSION_ERROR_FLAG:
    case educatorTypes.CLEAR_COURSE_UID:
      return {
        ...state,
        [action.key]: action.result
      };

    case `${educatorTypes.GET_ON_BOARDING_PREFERENCES}_SUCCESS`:
      return {
        ...state,
        onBoardingPreferences: {
          ...state.onBoardingPreferences,
          [action.goalUID]: action.result
        }
      };

    case `${educatorTypes.DELETE_ITEM}_REQUEST`:
      return { ...state, deleting: true };

    case `${educatorTypes.DELETE_ITEM}_SUCCESS`:
      return {
        ...state,
        deleting: false,
        sessions: state.sessions.filter(
          (session) => session.uid !== action.session.uid
        )
      };

    case `${educatorTypes.ADD_ITEM}_SUCCESS`:
      return {
        ...state,
        isSubmitting: false,
        newSession: initialState.newSession
      };

    case `${educatorTypes.DELETE_ITEM}_FAILURE`:
      return { ...state, deleting: false };

    case `${educatorTypes.ADD_ITEM}_FAILURE`:
    case `${educatorTypes.CREATE_PLUS_COURSE}_FAILURE`:
    case `${educatorTypes.EDIT_PLUS_COURSE}_FAILURE`:
    case `${educatorTypes.CREATE_TEST_SERIES_COURSE}_FAILURE`:
    case `${educatorTypes.EDIT_SPECIAL_CLASS}_FAILURE`:
    case `${educatorTypes.CREATE_SPECIAL_CLASS}_FAILURE`:
    case `${educatorTypes.EDIT_TEST_SERIES_COURSE}_FAILURE`:
    case `${educatorTypes.EDIT_TEMPLATE_COURSE}_FAILURE`:
    case `${educatorTypes.ADD_TEMPLATE_COURSE}_FAILURE`:
      return {
        ...state,
        isSubmitting: false,
        createEditError: action.error
      };

    case `${educatorTypes.CREATE_PLUS_COURSE}_SUCCESS`:
    case `${educatorTypes.FETCH_COURSE_DETAILS}_SUCCESS`:
    case `${educatorTypes.EDIT_PLUS_COURSE}_SUCCESS`:
    case `${educatorTypes.CREATE_TEST_SERIES_COURSE}_SUCCESS`:
    case `${educatorTypes.EDIT_TEST_SERIES_COURSE}_SUCCESS`:
      return {
        ...state,
        isSubmitting: false,
        createEditError: false,
        ...getData(action.result)
      };

    case `${educatorTypes.EDIT_SPECIAL_CLASS}_SUCCESS`:
    case `${educatorTypes.CREATE_SPECIAL_CLASS}_SUCCESS`:
    case `${educatorTypes.ADD_TEMPLATE_COURSE}_SUCCESS`:
      return {
        ...state,
        ...action.result,
        isSubmitting: false,
        createEditError: false
      };

    case `${educatorTypes.FETCH_TEMPLATE_COURSE_DETAILS}_SUCCESS`:
    case `${educatorTypes.FETCH_SPECIAL_CLASS_DETAILS}_FAILURE`:
    case `${educatorTypes.FETCH_SPECIAL_CLASS_DETAILS}_SUCCESS`:
    case educatorTypes.SETUP_TEMPLATE_DATA_IN_EDU_COURSE:
    case educatorTypes.EDIT_TEMPLATE_COURSE_LESSON_WITHOUT_UID:
      return {
        ...state,
        ...action.result
      };

    case `${educatorTypes.EDIT_SESSION}_REQUEST`:
    case `${educatorTypes.STATE_CHANGE}_REQUEST`:
    case `${educatorTypes.CREATE_PLUS_COURSE}_REQUEST`:
    case `${educatorTypes.EDIT_TEST_SERIES_COURSE}_REQUEST`:
    case `${educatorTypes.EDIT_PLUS_COURSE}_REQUEST`:
    case `${educatorTypes.CREATE_TEST_SERIES_COURSE}_REQUEST`:
    case `${educatorTypes.EDIT_SPECIAL_CLASS}_REQUEST`:
    case `${educatorTypes.CREATE_SPECIAL_CLASS}_REQUEST`:
    case `${educatorTypes.ADD_ITEM}_REQUEST`:
    case `${educatorTypes.UPLOAD_THUMBNAIL}_REQUEST`:
    case `${educatorTypes.CREATE_LIVE_SUPPORT_TICKET}_REQUEST`:
    case `${educatorTypes.ADD_TEMPLATE_COURSE}_REQUEST`:
    case `${educatorTypes.EDIT_TEMPLATE_COURSE}_REQUEST`:
      return {
        ...state,
        isSubmitting: true
      };

    case `${educatorTypes.UPLOAD_THUMBNAIL}_SUCCESS`:
      return {
        ...state,
        thumbnail: action.result.thumbnail_image,
        isSubmitting: false
      };

    case `${educatorTypes.FETCH_EDUCATOR_LEADERBOARD}_SUCCESS`:
    case `${educatorTypes.FETCH_EDUCATOR_LEADERBOARD}_FAILURE`:
    case `${educatorTypes.UPLOAD_THUMBNAIL}_FAILURE`:
    case `${educatorTypes.CREATE_LIVE_SUPPORT_TICKET}_SUCCESS`:
    case `${educatorTypes.CREATE_LIVE_SUPPORT_TICKET}_FAILURE`:
    case `${educatorTypes.EDIT_TEMPLATE_COURSE}_SUCCESS`:
      return {
        ...state,
        isSubmitting: false
      };

    case educatorTypes.CLEAR_ADD_ITEM_FORM:
      return {
        ...state,
        isSubmitting: false,
        newSession: initialState.newSession
      };

    case educatorTypes.CLEAR_COURSE_FIELDS:
      return { ...initialState };

    case `${educatorTypes.FETCH_COURSE_STATS}_SUCCESS`:
      return {
        ...state,
        courseDetails: { ...state.courseDetails, ...action.result }
      };

    case `${educatorTypes.FETCH_COURSE_RATINGS}_SUCCESS`:
      return {
        ...state,
        courseDetails: {
          ...state.courseDetails,
          courseMetrics: action.result,
          isCourseMetricsFetched: true
        }
      };

    case `${educatorTypes.FETCH_COURSE_FEEDBACK}_SUCCESS`:
      return {
        ...state,
        courseDetails: {
          ...state.courseDetails,
          courseFeedback: action.result.results,
          isCourseFeedbackFetched: true
        }
      };

    case `${educatorTypes.FETCH_LESSON_METRICS}_SUCCESS`:
      return {
        ...state,
        courseDetails: {
          ...state.courseDetails,
          lessonMetrics: {
            ...state.courseDetails.lessonMetrics,
            [action.lessonUID]: action.result
          },
          isLessonMetricsFetched: true
        }
      };

    case educatorTypes.UPDATE_RESCHEDULE_TEXT_FIELD:
    case educatorTypes.UPDATE_RESCHEDULE_DROPDOWN_FIELDS:
      return {
        ...state,
        courseDetails: {
          ...state.courseDetails,
          rescheduleDetails: {
            ...state.courseDetails.rescheduleDetails,
            [action.id]: action.multipleValues
              ? [...state[action.id], action.value]
              : action.value
          }
        }
      };

    case `${educatorTypes.RESCHEDULE_CLASS}_SUCCESS`:
      return {
        ...state,
        courseDetails: {
          ...state.courseDetails,
          rescheduleDetails: initialState.courseDetails.rescheduleDetails
        }
      };

    case educatorTypes.UPDATE_DISCUSSION_FORUM_TEXT:
      return {
        ...state,
        courseDetails: {
          ...state.courseDetails,
          discussionForum: {
            ...state.courseDetails.discussionForum,
            text: action.value
          }
        }
      };

    case educatorTypes.SET_FILTER:
      return {
        ...state,
        filter: { ...state.filter, [action.data.courseType]: action.data.value }
      };

    case educatorTypes.UPDATE_TEMPLATE_COURSE_FIELDS:
      return {
        ...state,
        schedule: {
          ...state.schedule,
          [action.id]: action.value
        }
      };

    case educatorTypes.UPDATE_SCHEDULE_WEEK_DAY: {
      const scheduleWeek = [...state.schedule.daysOfWeek];
      scheduleWeek[action.index] = action.value;
      return {
        ...state,
        schedule: {
          ...state.schedule,
          daysOfWeek: scheduleWeek
        }
      };
    }

    case educatorTypes.FILL_DURATION_IN_SCHEDULE:
      return {
        ...state,
        schedule: {
          ...state.schedule,
          duration: state.sessionDuration
        }
      };

    case educatorTypes.DELETE_TEMPLATE_LESSONS_FROM_COURSE:
      return {
        ...state,
        sessions: action.sessions,
        liveClasses: action.liveClasses,
        quizzes: action.quizzes
      };

    case educatorTypes.SET_CLASH_SESSIONS_OF_A_COURSE:
      return {
        ...state,
        clashSessions: action.data,
        areSessionsClashing: true
      };

    case educatorTypes.REMOVE_CLASH_SESSIONS:
      return {
        ...state,
        clashSessions: [],
        areSessionsClashing: false
      };

    case `${educatorTypes.SUBMIT_FEEDBACK_FOR_COURSE_TEMPLATE}_REQUEST`:
      return {
        ...state,
        isSubmittingFeedback: true,
        submitFeedbackFailed: false
      };

    case `${educatorTypes.SUBMIT_FEEDBACK_FOR_COURSE_TEMPLATE}_SUCCESS`:
      return {
        ...state,
        isSubmittingFeedback: false,
        submitFeedbackFailed: false
      };

    case `${educatorTypes.SUBMIT_FEEDBACK_FOR_COURSE_TEMPLATE}_FAILURE`:
      return {
        ...state,
        isSubmittingFeedback: false,
        submitFeedbackFailed: true
      };

    default:
      return state;
  }
};

export default reducer;
